import { defineStore } from "pinia";
import axios from "axios";
import { Dashboard, Organisation, Review, User } from "@/services";

import { mapPeriodToDateRange, formatTime, getGraphColor } from "@/utils";
import { deriveBenchmarkSet } from "@/data/benchmarkSet";

// The benchmark set was once persisted here, unscoped, so a set built for one
// company survived a switch to another. It is derived per company now; drop the
// dead key so it cannot be mistaken for live state.
localStorage.removeItem("selectedCompetitors");

export const useMainStore = defineStore("main", {
  state: () => ({
    /* User */
    userName: null,
    userEmail: null,
    userActivated: null,
    userAvatar: null,
    isSuperuser: false,
    userOrganisations: [],
    /* Demo mode */
    isDemoMode: false,
    demoIndustry: null,
    demoModalVisible: false,
    /* Field focus with ctrl+k (to register only once) */
    isFieldFocusRegistered: false,

    /* Sample data (commonly used) */
    clients: [],
    history: [],
    companiesWatched: [],
    locations: null,
    channels: null,
    overview: null,
    // Get values from local storage or use default values
    selectedLocation: localStorage.getItem("selectedLocation")
      ? JSON.parse(localStorage.getItem("selectedLocation"))
      : null,
    // store raw value; a getter named `selectedCompany` (below) will compute
    // the effective selected company when accessed and run the necessary
    // setup logic automatically.
    selectedCompanyRaw: localStorage.getItem("selectedCompany")
      ? JSON.parse(localStorage.getItem("selectedCompany"))
      : null,
    viewingCompetitor: localStorage.getItem("viewingCompetitor")
      ? JSON.parse(localStorage.getItem("viewingCompetitor"))
      : null,
    // New default is "Last 365 Days". A user with a persisted value (incl. the
    // old "All Time" default) keeps it — only the unset case picks up 365.
    selectedPeriod: localStorage.getItem("selectedPeriod") || "Last 365 Days",
    customStart: localStorage.getItem("customStart") || null,
    customEnd: localStorage.getItem("customEnd") || null,
    selectedAggregation:
      localStorage.getItem("selectedAggregation") || "Monthly",
    selectedTopic: null,
    selectedEntity: null,
    reviewsByPlatform: null,
    sentimentByPlatform: null,
    negativeReviewStats: null,
    reviewsByEmotion: null,
    negativeTopics: null,
    positiveTopics: null,
    reviews: [],
    wordFrequency: null,
    reviewCountByPlatform: null,
    loading: false,
    // localStorage so the active workspace survives reloads and new tabs;
    // sessionStorage fallback migrates selections persisted before this change.
    organisation:
      localStorage.getItem("organisation") ||
      sessionStorage.getItem("organisation") ||
      null,
    competitorData: [],
  }),
  getters: {
    // When `selectedCompany` is accessed this getter computes the effective
    // selected company (from local storage raw value or first watched company)
    // and performs the minimal setup that previously lived next to the state
    // initialization (populating `locations`, `channels` and default
    // `selectedLocation`). This keeps the behaviour lazy and runs only when the
    // store consumer accesses `selectedCompany`.
    // Keep getters pure and side-effect free. Any initialization or
    // persistence should be performed by an explicit action such as
    // `ensureSelectedCompany()` below.
    selectedCompany(state) {
      const raw = state.selectedCompanyRaw;
      const watched = state.companiesWatched || [];
      // A persisted selection may have been left behind by another account
      // that used this browser (a demo session, a previous login). Only trust
      // it once the watched list is loaded and confirms membership; before the
      // list loads we can't tell, so keep the raw value to avoid a flash of
      // "Select company" on every reload.
      const rawIsWatched =
        raw &&
        (!watched.length ||
          watched.some((c) => String(c.company_id) === String(raw.company_id)));
      if (raw && rawIsWatched) return raw;
      if (!watched.length) return null;
      // Prefer the active org's configured default company, else the first watched.
      const activeOrg = (state.userOrganisations || []).find(
        (o) =>
          String(o.org_id ?? o.organisation_id) === String(state.organisation),
      );
      const defaultId = activeOrg?.default_company_id;
      if (defaultId != null) {
        const match = watched.find(
          (c) => String(c.company_id) === String(defaultId),
        );
        if (match) return match;
      }
      return watched[0];
    },
    // True when the selected company has more than one real branch (the
    // location scope picker is only meaningful then). Excludes the synthetic
    // "ALL" entry that `populateSelectedCompanyLocations` always prepends.
    hasMultipleBranches(state) {
      const branches = (state.locations || []).filter(
        (l) => l.location_id !== "ALL",
      );
      return branches.length > 1;
    },
    // True when the selected company has at least one real location.
    // Excludes the synthetic "ALL" sentinel that
    // `populateSelectedCompanyLocations` always prepends.
    usesLocations(state) {
      return (state.locations || []).some((l) => l.location_id !== "ALL");
    },
    // Convenience object passed to mapPeriodToDateRange for the "Custom" period.
    customRange(state) {
      return { customStart: state.customStart, customEnd: state.customEnd };
    },
    // Stable key that changes whenever the effective date filter changes.
    // Watchers key off this instead of selectedPeriod alone so that switching
    // to a new custom range while already on "Custom" (selectedPeriod unchanged)
    // still triggers a refetch.
    dateFilterKey(state) {
      return `${state.selectedPeriod}|${state.customStart || ""}|${state.customEnd || ""}`;
    },
    isReadOnly(state) {
      return !!state.viewingCompetitor;
    },
    activeCompanyId(state) {
      const sel = this.selectedCompany;
      return state.viewingCompetitor?.company_id ?? sel?.company_id ?? null;
    },
    // The benchmark set follows the selected company's own CompetitorLink rows.
    // Derived, never persisted — see deriveBenchmarkSet.
    selectedCompetitors(state) {
      return deriveBenchmarkSet(this.selectedCompany, state.companiesWatched);
    },
  },
  actions: {
    setUser(payload) {
      if (payload === null) {
        this.userName = null;
        this.userEmail = null;
        this.userAvatar = null;
        this.organisation = null;
        this.userOrganisations = [];
        this.isDemoMode = false;
        this.demoIndustry = null;
        this.demoModalVisible = false;
        // Clear the workspace selection too: leaving it behind lets the next
        // account that logs in on this browser (e.g. a fresh signup after a
        // demo session) inherit another workspace's selected company.
        this.companiesWatched = [];
        this.selectedCompanyRaw = null;
        this.selectedLocation = null;
        this.viewingCompetitor = null;
        localStorage.removeItem("organisation");
        sessionStorage.removeItem("organisation");
        localStorage.removeItem("selectedCompany");
        localStorage.removeItem("selectedLocation");
        localStorage.removeItem("viewingCompetitor");
        return;
      }
      if (payload.name) {
        this.userName = payload.name;
      } else {
        this.userName = payload.email;
      }
      if (payload.email) {
        this.userEmail = payload.email;
      }
      if (payload.avatar) {
        this.userAvatar = payload.avatar;
      }

      if (payload.enabled) {
        this.userActivated = payload.enabled;
      }

      if (payload.is_superuser !== undefined) {
        this.isSuperuser = payload.is_superuser;
      }

      if (payload.is_demo !== undefined) {
        this.isDemoMode = payload.is_demo;
      }

      if (payload.demo_industry !== undefined) {
        this.demoIndustry = payload.demo_industry;
      }

      if (payload.organisations) {
        this.userOrganisations = payload.organisations;
      }

      if (payload.organisation !== undefined && payload.organisation !== null) {
        this.organisation = payload.organisation;
      }
    },

    // Pull the current user from the Django session into the store. Used after
    // self-service onboarding (signup / workspace creation) so the app reflects
    // the freshly authenticated user without a full page reload. Mirrors the
    // rehydration in main.js. Returns true if a user was loaded.
    async hydrateSession() {
      const userData = await User.getUser();
      if (!userData) return false;
      const organisations = userData?.organisations ?? [];
      // Prefer the persisted workspace (survives reloads) as long as the user
      // still belongs to it; only fall back to the first organisation when
      // there is no valid persisted choice.
      const persisted =
        this.organisation ??
        localStorage.getItem("organisation") ??
        sessionStorage.getItem("organisation");
      const persistedIsValid =
        persisted != null &&
        organisations.some(
          (o) => String(o.org_id ?? o.organisation_id) === String(persisted),
        );
      const firstOrg = organisations[0];
      const organisationId = persistedIsValid
        ? persisted
        : (firstOrg?.org_id ??
          firstOrg?.organisation_id ??
          userData?.organisation_id ??
          userData?.organisation ??
          null);
      // setOrganisation must run before setUser: it detects an organisation
      // *change* (and wipes the persisted company selection) by comparing
      // against the previous value, which setUser overwrites.
      if (organisationId) {
        this.setOrganisation(organisationId);
      }
      this.setUser({
        name: userData.username,
        email: userData.email,
        organisation: organisationId,
        enabled: true,
        organisations,
        is_superuser: userData.is_superuser,
        is_demo: userData.is_demo ?? false,
        demo_industry: userData.demo_industry ?? null,
      });
      if (organisationId) {
        await Organisation.select(organisationId);
        await this.fetchCompaniesWatched();
      }
      return true;
    },

    setOrganisation(organisationId) {
      const changed =
        String(this.organisation ?? "") !== String(organisationId ?? "");
      if (!organisationId) {
        this.organisation = null;
        localStorage.removeItem("organisation");
      } else {
        this.organisation = organisationId;
        localStorage.setItem("organisation", String(organisationId));
      }
      // Selection used to live in sessionStorage; clear the legacy key so it
      // can't shadow the localStorage value.
      sessionStorage.removeItem("organisation");

      // The company selection belongs to an organisation — it only becomes
      // stale when the organisation actually changes. Re-asserting the current
      // organisation (e.g. the boot rehydration on every page load) must not
      // wipe the user's persisted workspace selection.
      if (changed) {
        this.selectedCompanyRaw = null;
        localStorage.removeItem("selectedCompany");
      }
    },

    async fetchOverviewReviewStats() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );
      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      const companyID = this.activeCompanyId;
      if (!companyID) return;

      const data = await Review.getOverviewStatsByCompanyID(
        companyID,
        formattedStartDate,
        formattedEndDate,
      );

      if (!data || typeof data !== "object") return;

      const stats = data;

      const formatTrend = (value) => {
        if (value === null || value === undefined)
          return { trend: null, trendType: null };
        const sign = value >= 0 ? "+" : "";
        return {
          trend: `${sign}${value}%`,
          trendType: value >= 0 ? "up" : "down",
        };
      };

      this.overview = {
        reviews: {
          total: stats.reviews_count,
          ...formatTrend(stats.reviews_count_trend),
        },
        sentiment: {
          total:
            stats.avg_sentiment !== null
              ? Math.round(stats.avg_sentiment * 100)
              : null,
          ...formatTrend(stats.avg_sentiment_trend),
        },
        ratings: {
          total: stats.ratings_count,
          ...formatTrend(stats.ratings_count_trend),
        },
        avgRating: {
          total:
            stats.avg_rating !== null
              ? Math.round(stats.avg_rating * 100) / 100
              : null,
          ...formatTrend(stats.avg_rating_trend),
        },
        avgResponseTime: (() => {
          const formattedTime =
            stats.median_response_time_hrs !== null
              ? formatTime(stats.median_response_time_hrs * 60)
              : null;
          return {
            total: formattedTime?.isValid ? formattedTime.value : "-",
            suffix: formattedTime?.isValid ? formattedTime.suffix : "",
            ...formatTrend(stats.median_response_time_hrs_trend),
          };
        })(),
      };
    },

    async setSelectedTopic(payload) {
      this.selectedTopic = payload;
    },

    async setSelectedEntity(payload) {
      this.selectedEntity = payload;
    },

    // Fetch competitor stats for the selected competitors.
    // By default this updates `this.competitorData` in the store, but
    // callers can pass `{ setState: false }` to receive the fetched data
    // without mutating the store. This makes it safer for components to
    // fetch data without triggering store watchers and potential loops.
    async updateCompetitorData({ setState = true } = {}) {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );
      // Ensure API gets ISO strings like other fetchers in this store
      const dateFrom = startDate ? startDate.toISOString() : null;
      const dateTo = endDate ? endDate.toISOString() : null;

      const companyIDs = (this.selectedCompetitors || [])
        .map((company) => company.company_id)
        .filter(Boolean);

      if (companyIDs.length === 0) {
        if (setState) this.competitorData = [];
        return [];
      }

      const data = await Review.getStatsByCompanyIDs(
        companyIDs,
        dateFrom,
        dateTo,
      );

      if (setState) {
        this.competitorData = data;
      }

      return data;
    },

    async setSelectedLocation(payload) {
      this.selectedLocation = payload;
      localStorage.setItem("selectedLocation", JSON.stringify(payload)); // Save to local storage
    },

    async setSelectedCompany(payload) {
      this.selectedCompanyRaw = payload;
      localStorage.setItem("selectedCompany", JSON.stringify(payload)); // Save to local storage
      this.exitCompetitor();
      this.populateSelectedCompanyLocations();
    },

    viewCompetitor(competitor) {
      this.viewingCompetitor = competitor;
      localStorage.setItem("viewingCompetitor", JSON.stringify(competitor));
      // Competitor view is company-wide: reset the location scope so a stale
      // owned-company branch filter doesn't filter competitor reads to nothing.
      this._resetLocationScope();
    },

    exitCompetitor() {
      this.viewingCompetitor = null;
      localStorage.removeItem("viewingCompetitor");
      this._resetLocationScope();
    },

    _resetLocationScope() {
      const all = {
        id: "ALL",
        location_id: "ALL",
        name: "All locations",
        label: "All locations",
      };
      this.selectedLocation = all;
      localStorage.setItem("selectedLocation", JSON.stringify(all));
    },

    async setSelectedPeriod(period) {
      this.selectedPeriod = period;
      localStorage.setItem("selectedPeriod", period); // Save to local storage
      // Individual components will handle their own data fetching via watchers
    },

    async setCustomRange({ customStart, customEnd }) {
      this.customStart = customStart || null;
      this.customEnd = customEnd || null;
      if (this.customStart)
        localStorage.setItem("customStart", this.customStart);
      else localStorage.removeItem("customStart");
      if (this.customEnd) localStorage.setItem("customEnd", this.customEnd);
      else localStorage.removeItem("customEnd");
    },

    async setAggregation(aggregation) {
      this.selectedAggregation = aggregation;
      localStorage.setItem("selectedAggregation", aggregation); // Save to local storage
      // Individual components will handle their own data fetching via watchers
    },

    fetch(sampleDataKey) {
      axios
        .get(`data-sources/${sampleDataKey}.json`)
        .then((r) => {
          if (r.data && r.data.data) {
            this[sampleDataKey] = r.data.data;
          }
        })
        .catch((error) => {
          alert(error.message);
        });
    },

    async fetchReviewsByPlatform() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const data = await Review.getCountByPlatformByMonth(
        this.activeCompanyId,
        this.selectedAggregation.toLowerCase(),
        startDate.toISOString(),
        endDate.toISOString(),
      );

      if (data) {
        this.reviewCountByPlatform = data;
      }
    },

    async setReviewCountByPlatform() {
      // Check if required data is available
      if (!this.reviewsByPlatform) {
        console.warn(
          "Required data not available for setReviewCountByPlatform",
        );
        return;
      }

      const chartData = {
        platforms: [],
        dates: [],
        series: [],
      };

      const platformSet = new Set();
      const dateSet = new Set();

      this.reviewsByPlatform.forEach((item) => {
        platformSet.add(item.source);
        dateSet.add(item.date);
      });

      if (this.selectedAggregation === "Daily") {
      } else if (this.selectedAggregation === "Weekly") {
      } else if (this.selectedAggregation === "Monthly") {
      } else if (this.selectedAggregation === "Quarterly") {
      } else if (this.selectedAggregation === "Yearly") {
      }

      chartData.platforms = Array.from(platformSet);
      chartData.dates = Array.from(dateSet);

      // Convert to month/year labels if the date range is greater than 90 days

      const seriesData = {};
      chartData.platforms.forEach((platform) => {
        seriesData[platform] = Array(chartData.dates.length).fill(0);
      });

      this.reviewsByPlatform.forEach((item) => {
        const indexDate = chartData.dates.indexOf(item.date);
        const indexPlatform = chartData.platforms.indexOf(item.source);
        seriesData[item.source][indexDate] = item.ReviewCount;
      });

      chartData.series = chartData.platforms.map((platform) => ({
        label: platform.charAt(0).toUpperCase() + platform.slice(1),
        data: seriesData[platform],
        backgroundColor: getGraphColor(platform),
      }));

      this.reviewCountByPlatform = chartData;
    },

    async fetchNegativeReviewStats() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const data = await Review.getPercentageNegative(
        this.activeCompanyId,
        this.selectedAggregation.toLowerCase(),
        startDate.toISOString(),
        endDate.toISOString(),
      );
      this.negativeReviewStats = data;
    },

    async fetchSentimentByPlatform() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      const sentimentByPlatform = await Review.getSentimentBySource(
        this.activeCompanyId,
        formattedStartDate,
        formattedEndDate,
      );
      this.sentimentByPlatform = sentimentByPlatform;
    },

    async fetchReviewsByEmotion() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      const reviewsByEmotion = await Review.getTopEmotions(
        this.activeCompanyId,
        formattedStartDate,
        formattedEndDate,
      );
      this.reviewsByEmotion = reviewsByEmotion;
      this.reviewsByEmotion.emotionLabels = reviewsByEmotion.map(
        (entry) => entry.Emotion,
      );
      this.reviewsByEmotion.emotionSeries = [
        { data: reviewsByEmotion.map((entry) => entry.ReviewCount) },
      ];
    },

    async fetchNegativeTopics() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      const negativeTopics = await Review.getNegativeTopics(
        this.activeCompanyId,
        formattedStartDate,
        formattedEndDate,
      );
      if (negativeTopics) {
        negativeTopics.sort((a, b) => b.count - a.count);
        this.negativeTopics = negativeTopics;
      }
    },

    async fetchPositiveTopics() {
      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );

      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();

      const positiveTopics = await Review.getPositiveTopics(
        this.activeCompanyId,
        formattedStartDate,
        formattedEndDate,
      );
      if (positiveTopics) {
        positiveTopics.sort((a, b) => b.count - a.count);
        this.positiveTopics = positiveTopics;
      }
    },

    async fetchReviews() {
      const companyId = this.activeCompanyId;
      if (!companyId) return;

      const { startDate, endDate } = mapPeriodToDateRange(
        this.selectedPeriod,
        this.customRange,
      );
      const dateFrom = startDate.toISOString();
      const dateTo = endDate.toISOString();

      this.reviews = await Review.getRecentReviews(companyId, dateFrom, dateTo);
    },

    async fetchCompaniesWatched() {
      this.companiesWatched = await Organisation.getCompaniesWatched();
      await this.ensureSelectedCompany();
    },

    populateSelectedCompanyLocations() {
      const company = this.selectedCompany;
      const locations = company?.locations || [];
      const branchLocations = locations.map((location) => ({
        ...location,
        id: location.location_id,
        label: location.name,
      }));

      this.channels = company?.channels || [];
      this.locations = [
        {
          id: "ALL",
          location_id: "ALL",
          name: "All locations",
          label: "All locations",
        },
        ...branchLocations,
      ];

      const selectedId =
        this.selectedLocation?.location_id || this.selectedLocation?.id;
      const stillExists = this.locations.some(
        (location) =>
          String(location.location_id || location.id) === String(selectedId),
      );

      if (!this.selectedLocation || !stillExists) {
        this.selectedLocation = this.locations[0];
        localStorage.setItem(
          "selectedLocation",
          JSON.stringify(this.selectedLocation),
        );
      }
    },

    async ensureSelectedCompany() {
      // Drop a persisted selection the current account cannot access — e.g.
      // one left behind by a demo session or another login in this browser.
      // The selectedCompany getter already ignores it; clearing here keeps the
      // persisted state converged with what the getter resolves.
      const stale =
        this.selectedCompanyRaw &&
        (this.companiesWatched || []).length &&
        !(this.companiesWatched || []).some(
          (c) =>
            String(c.company_id) === String(this.selectedCompanyRaw.company_id),
        );
      if (stale) {
        this.selectedCompanyRaw = null;
        localStorage.removeItem("selectedCompany");
      }
      if (!this.selectedCompanyRaw) {
        // With no explicit selection, the selectedCompany getter resolves the
        // active org's default company (falling back to the first watched).
        // Reuse it so we don't override the default with companiesWatched[0].
        const resolved = this.selectedCompany;
        if (resolved) {
          this.selectedCompanyRaw = resolved;
          try {
            localStorage.setItem("selectedCompany", JSON.stringify(resolved));
          } catch (e) {
            // ignore storage errors
          }
        }
      }

      if (this.selectedCompanyRaw) {
        this.populateSelectedCompanyLocations();
      }
    },
  },
});
